import { useEffect, useMemo, useState } from "react";
import credibilityFallback from "../../data/credibility.json";
import { useCmsDoc } from "../../hooks/useCmsDoc";
import {
  Field,
  SaveBar,
  inputClass,
  textareaClass,
  saveCmsDoc,
  uploadCmsImage,
} from "../cmsApi.jsx";
import CmsWithMediaSpecs from "../components/CmsMediaSpecsPanel.jsx";
import { MoveButtons, moveListItem } from "../components/MoveListControls.jsx";

function useSection(docId, fallback) {
  const stable = useMemo(() => fallback, []);
  return useCmsDoc("credibility", docId, stable);
}

export default function CmsCredibilityEditor() {
  const providersHook = useSection("providers", credibilityFallback.providers);
  const testimonialsHook = useSection("testimonials", credibilityFallback.testimonials);
  const accreditationsHook = useSection("accreditations", {
    ...credibilityFallback.accreditations,
    highlights: (credibilityFallback.accreditations.highlights || []).map((h) => ({
      logo: "",
      ...h,
    })),
  });
  const pageHook = useSection("page", credibilityFallback.page);

  const [providers, setProviders] = useState(null);
  const [testimonials, setTestimonials] = useState(null);
  const [accreditations, setAccreditations] = useState(null);
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (providersHook.data) setProviders(structuredClone(providersHook.data));
  }, [providersHook.data]);
  useEffect(() => {
    if (testimonialsHook.data) setTestimonials(structuredClone(testimonialsHook.data));
  }, [testimonialsHook.data]);
  useEffect(() => {
    if (accreditationsHook.data) setAccreditations(structuredClone(accreditationsHook.data));
  }, [accreditationsHook.data]);
  useEffect(() => {
    if (pageHook.data) setPage(structuredClone(pageHook.data));
  }, [pageHook.data]);

  if (
    providersHook.loading ||
    testimonialsHook.loading ||
    accreditationsHook.loading ||
    pageHook.loading ||
    !providers ||
    !testimonials ||
    !accreditations ||
    !page
  ) {
    return <p className="text-sm text-brand-dark/50">Cargando Credibilidad…</p>;
  }

  const stripMeta = (obj) => {
    const { id, updatedAt, updatedBy, ...rest } = obj;
    return rest;
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await Promise.all([
        saveCmsDoc("credibility", "page", stripMeta(page)),
        saveCmsDoc("credibility", "providers", stripMeta(providers)),
        saveCmsDoc("credibility", "testimonials", stripMeta(testimonials)),
        saveCmsDoc("credibility", "accreditations", stripMeta(accreditations)),
      ]);
      setMessage("Credibilidad guardada correctamente.");
    } catch (err) {
      setError(err?.message || "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (section, file, apply) => {
    try {
      const url = await uploadCmsImage(section, file);
      apply(url);
    } catch (err) {
      setError(err?.message || "Error al subir imagen.");
    }
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h2 className="font-display text-3xl font-black">Credibilidad</h2>
        <p className="text-sm text-brand-dark/55 mt-1">
          Fuente: {providersHook.source} / {testimonialsHook.source} / {accreditationsHook.source}
        </p>
      </div>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Encabezado</h3>
        <Field label="Eyebrow">
          <input className={inputClass} value={page.eyebrow || ""} onChange={(e) => setPage({ ...page, eyebrow: e.target.value })} />
        </Field>
        <Field label="Título">
          <input className={inputClass} value={page.title || ""} onChange={(e) => setPage({ ...page, title: e.target.value })} />
        </Field>
        <Field label="Acento">
          <input className={inputClass} value={page.titleAccent || ""} onChange={(e) => setPage({ ...page, titleAccent: e.target.value })} />
        </Field>
        <Field label="Descripción">
          <textarea className={textareaClass} value={page.description || ""} onChange={(e) => setPage({ ...page, description: e.target.value })} />
        </Field>
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-lg">Proveedores</h3>
            <p className="text-sm text-brand-dark/55 mt-1">
              Use las flechas para ordenar. El sitio público muestra el mismo orden tras Guardar.
            </p>
          </div>
          <button
            type="button"
            className="text-xs font-bold uppercase tracking-widest text-brand-primary"
            onClick={() =>
              setProviders((prev) => ({
                ...prev,
                items: [
                  ...(prev.items || []),
                  { id: `brand-${Date.now()}`, name: "Nuevo proveedor", logo: "", website: "" },
                ],
              }))
            }
          >
            + Proveedor
          </button>
        </div>
        {(providers.items || []).map((item, idx) => (
          <div key={item.id || idx} className="border border-brand-soft rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <p className="text-xs font-bold uppercase text-brand-dark/40 w-8">#{idx + 1}</p>
                <MoveButtons
                  idx={idx}
                  total={(providers.items || []).length}
                  onMove={(dir) =>
                    setProviders((prev) => ({
                      ...prev,
                      items: moveListItem(prev.items || [], idx, dir),
                    }))
                  }
                />
              </div>
              <button
                type="button"
                className="text-xs text-red-600 font-bold"
                onClick={() =>
                  setProviders((prev) => ({
                    ...prev,
                    items: prev.items.filter((_, i) => i !== idx),
                  }))
                }
              >
                Quitar
              </button>
            </div>
            <input
              className={inputClass}
              value={item.name || ""}
              placeholder="Nombre"
              onChange={(e) => {
                const items = [...providers.items];
                items[idx] = { ...items[idx], name: e.target.value };
                setProviders({ ...providers, items });
              }}
            />
            <input
              className={inputClass}
              value={item.website || ""}
              placeholder="Website"
              onChange={(e) => {
                const items = [...providers.items];
                items[idx] = { ...items[idx], website: e.target.value };
                setProviders({ ...providers, items });
              }}
            />
            <CmsWithMediaSpecs variant="logos" sticky={false}>
              <input
                className={inputClass}
                value={item.logo || ""}
                placeholder="URL logo"
                onChange={(e) => {
                  const items = [...providers.items];
                  items[idx] = { ...items[idx], logo: e.target.value };
                  setProviders({ ...providers, items });
                }}
              />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  uploadLogo("credibility", file, (url) => {
                    const items = [...providers.items];
                    items[idx] = { ...items[idx], logo: url };
                    setProviders({ ...providers, items });
                  });
                }}
              />
              {item.logo && <img src={item.logo} alt="" className="h-16 object-contain" />}
            </CmsWithMediaSpecs>
          </div>
        ))}
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-lg">Testimonios</h3>
            <p className="text-sm text-brand-dark/55 mt-1">Use las flechas para ordenar.</p>
          </div>
          <button
            type="button"
            className="text-xs font-bold uppercase tracking-widest text-brand-primary"
            onClick={() =>
              setTestimonials((prev) => ({
                ...prev,
                items: [
                  ...(prev.items || []),
                  {
                    id: `t-${Date.now()}`,
                    quote: "",
                    author: "",
                    company: "",
                    logo: "",
                  },
                ],
              }))
            }
          >
            + Testimonio
          </button>
        </div>
        {(testimonials.items || []).map((item, idx) => (
          <div key={item.id || idx} className="border border-brand-soft rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <p className="text-xs font-bold uppercase text-brand-dark/40 w-8">#{idx + 1}</p>
                <MoveButtons
                  idx={idx}
                  total={(testimonials.items || []).length}
                  onMove={(dir) =>
                    setTestimonials((prev) => ({
                      ...prev,
                      items: moveListItem(prev.items || [], idx, dir),
                    }))
                  }
                />
              </div>
              <button
                type="button"
                className="text-xs text-red-600 font-bold"
                onClick={() =>
                  setTestimonials((prev) => ({
                    ...prev,
                    items: prev.items.filter((_, i) => i !== idx),
                  }))
                }
              >
                Quitar
              </button>
            </div>
            <textarea
              className={textareaClass}
              value={item.quote || ""}
              placeholder="Cita"
              onChange={(e) => {
                const items = [...testimonials.items];
                items[idx] = { ...items[idx], quote: e.target.value };
                setTestimonials({ ...testimonials, items });
              }}
            />
            <input
              className={inputClass}
              value={item.author || ""}
              placeholder="Autor"
              onChange={(e) => {
                const items = [...testimonials.items];
                items[idx] = { ...items[idx], author: e.target.value };
                setTestimonials({ ...testimonials, items });
              }}
            />
            <input
              className={inputClass}
              value={item.company || ""}
              placeholder="Empresa"
              onChange={(e) => {
                const items = [...testimonials.items];
                items[idx] = { ...items[idx], company: e.target.value };
                setTestimonials({ ...testimonials, items });
              }}
            />
            <CmsWithMediaSpecs variant="logos" sticky={false}>
              <input
                className={inputClass}
                value={item.logo || ""}
                placeholder="URL logo"
                onChange={(e) => {
                  const items = [...testimonials.items];
                  items[idx] = { ...items[idx], logo: e.target.value };
                  setTestimonials({ ...testimonials, items });
                }}
              />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  uploadLogo("credibility", file, (url) => {
                    const items = [...testimonials.items];
                    items[idx] = { ...items[idx], logo: url };
                    setTestimonials({ ...testimonials, items });
                  });
                }}
              />
            </CmsWithMediaSpecs>
          </div>
        ))}
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-lg">Acreditaciones (con logo)</h3>
            <p className="text-sm text-brand-dark/55 mt-1">Use las flechas para ordenar.</p>
          </div>
          <button
            type="button"
            className="text-xs font-bold uppercase tracking-widest text-brand-primary"
            onClick={() =>
              setAccreditations((prev) => ({
                ...prev,
                highlights: [
                  ...(prev.highlights || []),
                  {
                    id: `acc-${Date.now()}`,
                    title: "Nueva acreditación",
                    description: "",
                    logo: "",
                  },
                ],
              }))
            }
          >
            + Acreditación
          </button>
        </div>
        <Field label="URL catálogo Heyzine">
          <input
            className={inputClass}
            value={accreditations.catalog?.embedUrl || ""}
            onChange={(e) =>
              setAccreditations({
                ...accreditations,
                catalog: { ...accreditations.catalog, embedUrl: e.target.value },
              })
            }
          />
        </Field>
        {(accreditations.highlights || []).map((item, idx) => (
          <div key={item.id || idx} className="border border-brand-soft rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <p className="text-xs font-bold uppercase text-brand-dark/40 w-8">#{idx + 1}</p>
                <MoveButtons
                  idx={idx}
                  total={(accreditations.highlights || []).length}
                  onMove={(dir) =>
                    setAccreditations((prev) => ({
                      ...prev,
                      highlights: moveListItem(prev.highlights || [], idx, dir),
                    }))
                  }
                />
              </div>
              <button
                type="button"
                className="text-xs text-red-600 font-bold"
                onClick={() =>
                  setAccreditations((prev) => ({
                    ...prev,
                    highlights: prev.highlights.filter((_, i) => i !== idx),
                  }))
                }
              >
                Quitar
              </button>
            </div>
            <input
              className={inputClass}
              value={item.title || ""}
              placeholder="Título"
              onChange={(e) => {
                const highlights = [...accreditations.highlights];
                highlights[idx] = { ...highlights[idx], title: e.target.value };
                setAccreditations({ ...accreditations, highlights });
              }}
            />
            <textarea
              className={textareaClass}
              value={item.description || ""}
              placeholder="Descripción"
              onChange={(e) => {
                const highlights = [...accreditations.highlights];
                highlights[idx] = { ...highlights[idx], description: e.target.value };
                setAccreditations({ ...accreditations, highlights });
              }}
            />
            <CmsWithMediaSpecs variant="logos" sticky={false}>
              <input
                className={inputClass}
                value={item.logo || ""}
                placeholder="URL logo"
                onChange={(e) => {
                  const highlights = [...accreditations.highlights];
                  highlights[idx] = { ...highlights[idx], logo: e.target.value };
                  setAccreditations({ ...accreditations, highlights });
                }}
              />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  uploadLogo("credibility", file, (url) => {
                    const highlights = [...accreditations.highlights];
                    highlights[idx] = { ...highlights[idx], logo: url };
                    setAccreditations({ ...accreditations, highlights });
                  });
                }}
              />
              {item.logo && <img src={item.logo} alt="" className="h-16 object-contain" />}
            </CmsWithMediaSpecs>
          </div>
        ))}
      </section>

      <SaveBar saving={saving} onSave={onSave} message={message} error={error} />
    </div>
  );
}
